/**
 * MyPlansTab – shows customer's current subscription and pending requests.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Clock, CheckCircle, XCircle, RefreshCw, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function daysLeft(endIso) {
  if (!endIso) return null;
  const diff = new Date(endIso) - new Date();
  return Math.max(0, Math.ceil(diff / 86400000));
}

const STATUS_CFG = {
  active:   { label: "Active",              cls: "bg-green-50 text-green-700 border-green-200",  Icon: CheckCircle },
  pending:  { label: "Pending Approval",    cls: "bg-yellow-50 text-yellow-700 border-yellow-200", Icon: Clock },
  rejected: { label: "Rejected",            cls: "bg-red-50 text-red-600 border-red-200",        Icon: XCircle },
  expired:  { label: "Expired",             cls: "bg-slate-50 text-slate-500 border-slate-200",  Icon: AlertTriangle },
  cancelled:{ label: "Cancelled",           cls: "bg-slate-50 text-slate-500 border-slate-200",  Icon: XCircle },
};

function SubCard({ sub, isActive }) {
  const cfg  = STATUS_CFG[sub.status] || STATUS_CFG.pending;
  const days = isActive ? daysLeft(sub.end_date) : null;
  const used  = sub.downloads_used   || 0;
  const limit = sub.downloads_limit  || 0;

  return (
    <div className={`border rounded-2xl p-5 ${isActive ? "border-orange-200 bg-orange-50/30" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Crown className={`w-5 h-5 ${isActive ? "text-orange-500" : "text-slate-300"}`} />
          <h3 className="font-bold text-slate-800">{sub.plan_name}</h3>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${cfg.cls}`}>
          {cfg.label}
        </span>
      </div>

      {isActive && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Downloads used</span>
            <span className="font-bold">{used} / {limit === -1 ? "Unlimited" : limit}</span>
          </div>
          {limit !== -1 && (
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-2 bg-orange-500 rounded-full"
                style={{ width: `${Math.min(100, (used / Math.max(limit, 1)) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs">
        {sub.start_date && (
          <div>
            <p className="text-slate-400">Start Date</p>
            <p className="font-semibold text-slate-700">{fmtDate(sub.start_date)}</p>
          </div>
        )}
        {sub.end_date && (
          <div>
            <p className="text-slate-400">Expiry</p>
            <p className={`font-semibold ${days !== null && days <= 7 ? "text-red-600" : "text-slate-700"}`}>
              {fmtDate(sub.end_date)}
              {days !== null && days <= 7 && ` (${days}d left)`}
            </p>
          </div>
        )}
        <div>
          <p className="text-slate-400">Applied on</p>
          <p className="font-semibold text-slate-700">{fmtDate(sub.created_at)}</p>
        </div>
        {sub.status === "pending" && (
          <div>
            <p className="text-slate-400">Payment</p>
            <p className="font-semibold text-slate-700 capitalize">{sub.payment_method?.replace("_"," ")}</p>
          </div>
        )}
      </div>

      {sub.status === "pending" && (
        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-xl px-3 py-2 text-xs text-yellow-700">
          Your payment is being verified. Once confirmed by the admin, your plan will activate automatically.
        </div>
      )}
      {sub.status === "rejected" && sub.rejection_reason && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-600">
          Reason: {sub.rejection_reason}
        </div>
      )}
    </div>
  );
}

export default function MyPlansTab({ token, onEnrollClick }) {
  const [subInfo, setSubInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, histRes] = await Promise.all([
        axios.get(`${API}/customer/subscription`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customer/subscriptions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setSubInfo(subRes.data);
      setHistory(histRes.data);
    } catch (e) {
      toast.error("Failed to load plan info");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  const hasPlan = subInfo?.has_plan;
  const activeSub = subInfo?.subscription;
  const freePlan  = subInfo?.free_plan;

  return (
    <div className="space-y-4" data-testid="my-plans-tab">
      {hasPlan && activeSub ? (
        <SubCard sub={activeSub} isActive />
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center space-y-3">
          <Crown className="w-10 h-10 text-slate-200 mx-auto" />
          <h3 className="font-bold text-slate-700">
            {freePlan ? `You're on ${freePlan.name}` : "No Active Plan"}
          </h3>
          <p className="text-sm text-slate-400">
            {freePlan
              ? `Free plan includes ${freePlan.download_limit} product download${freePlan.download_limit !== 1 ? "s" : ""}. Upgrade for more.`
              : "Subscribe to a plan to download more product data."}
          </p>
          <button
            data-testid="enroll-plan-btn"
            onClick={onEnrollClick}
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-xl transition-colors text-sm">
            View Plans & Subscribe
          </button>
        </div>
      )}

      {/* Subscription history from admin table (filtered to this customer) */}
      {history.filter(s => s.status !== "active").length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Previous Requests</h3>
          <div className="space-y-3">
            {history.filter(s => s.status !== "active").map(sub => (
              <SubCard key={sub.id} sub={sub} isActive={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
